import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { canAssignAnyCourse } from '../utils/coursePermissions';
import logo from '../logo2.svg';
import api from '../services/api';
import { resolveCompanyLogo, logoUrl } from '../utils/companyLogo';
import { useTheme } from '../context/ThemeContext';
import SignatureSetupModal from './SignatureSetupModal';
import ChangePasswordModal from './ChangePasswordModal';
import CNPModal from './CNPModal';
import SigningKeyActivationModal from './SigningKeyActivationModal';
import GDPRModal from './GDPRModal';
import ImpersonationBanner from './ImpersonationBanner';
import ChangeSignaturePopup from './ChangeSignaturePopup';
import OnboardingWizard from './onboarding/OnboardingWizard';
import GlobalSearch from './GlobalSearch';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import { RoleBadge, UserLabelBadges } from './ui/Badge';
import NavSection from './ui/NavSection';

// SVG Icons for sidebar navigation
const NavIcons = {
  dashboard: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  ),
  companies: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  ssmCompany: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3l8 4v5c0 4.418-3.582 8-8 9-4.418-1-8-4.582-8-9V7l8-4z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  ),
  courses: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  ),
  documents: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>
  ),
  search: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  ),
  reports: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
  assign: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
  sheets: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  employees: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  certificates: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  ),
  profile: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  ),
  sign: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  ),
  logout: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  ),
  semafor: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <circle cx="12" cy="7" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="17" r="1.5" />
    </svg>
  ),
  manual: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
      <circle cx="18" cy="18" r="3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 16.5v1.5l1 0.5" />
    </svg>
  ),
};

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(null);
  // Pasul de „semnătură" combină în același modal activarea AdES dacă userul nu are
  // încă o cheie generată — așa evităm două pop-up-uri consecutive la onboarding.
  const [signatureNeedsAdES, setSignatureNeedsAdES] = useState(false);
  // Global search modal — Ctrl+K (sau Cmd+K) sau iconiță în topbar.
  const [searchOpen, setSearchOpen] = useState(false);

  // Listener global Ctrl+K — montat o singură dată, indiferent de pagină.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const managesDepartment = !!user?.managesDepartment;
  const { theme } = useTheme();

  const [issuesCriticalCount, setIssuesCriticalCount] = useState(0);

  // Polling lejer la countul de probleme critice (la 60s) — pentru badge-ul roșu din meniu
  useEffect(() => {
    if (!user) return;
    const fetchCount = () => {
      api
        .get('/issues')
        .then((res) => setIssuesCriticalCount(res.data?.criticalCount || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Cele două zone au SEMANTICĂ FIXĂ, nu depind de rol:
  //  • „Dreapta sus, lângă butonul de temă" = locul logo-ului de FIRMĂ CLIENT
  //  • „Jos-stânga în sidebar" = locul logo-ului de FIRMĂ SSM
  //
  // Reguli de derivare:
  //  - User la firmă SSM (type=SSM) → logo-ul firmei apare jos-stânga, dreapta e gol
  //  - User la firmă CLIENT cu SSM parent → logo client dreapta sus + logo SSM parent jos-stânga
  //  - User la firmă CLIENT standalone (fără SSM parent) → doar logo client dreapta sus
  //  - SUPER_ADMIN → fără firmă, fără logo-uri
  const ownCompany = user?.company || null;
  const isSSMFirm = ownCompany?.type === 'SSM';
  const clientCompany = !isSSMFirm ? ownCompany : null;
  const ssmFirmCompany = isSSMFirm ? ownCompany : ownCompany?.parentCompany || null;

  const clientLogoPath = resolveCompanyLogo(clientCompany, theme);
  const ssmLogoPath = resolveCompanyLogo(ssmFirmCompany, theme);

  useEffect(() => {
    checkFirstTimeSetup();
    // Dependență pe id — nu pe `user` întreg — altfel orice `refreshUser()` (ex: după upload
    // logo) re-declanșează prompt-urile de onboarding la fiecare schimbare de câmp.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Re-rulează `checkFirstTimeSetup` când userul revine pe DASHBOARD după ce a completat un
  // sub-step din onboarding pe altă pagină (ex: traseul pe `/my-route`). DOAR la dashboard —
  // dacă re-rulăm pe alte rute, wizard-ul îl întrerupe pe user care încă configurează ceva
  // (ex: când ajunge pe /my-route, useEffect-ul fugea checkFirstTimeSetup care vedea că route
  // step nu mai blochează — am ajuns pe /my-route — și deschidea wizard-ul direct peste pagina
  // unde user-ul tocmai configura traseul). Acum așteptăm până userul ajunge pe /dashboard
  // (după ce MyRoute navighează acolo la save).
  useEffect(() => {
    if (!user) return;
    if (location.pathname !== '/dashboard') return;
    if (sessionStorage.getItem('continueOnboardingAfterRoute') === 'true') {
      sessionStorage.removeItem('continueOnboardingAfterRoute');
      checkFirstTimeSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.id]);

  const checkFirstTimeSetup = async (freshUser) => {
    // freshUser permite bypass-ul closure-ului stale după refreshUser()
    // (ex: după acceptare GDPR, state-ul React nu e încă actualizat în closure).
    const u = freshUser || user;
    if (!u) return;
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

    // Step 0: GDPR — obligatoriu înainte de orice altceva (userul acceptă
    // prelucrarea datelor înainte să introducă CNP, semnătură, etc.).
    // Se afișează dacă nu a acceptat niciodată SAU dacă versiunea a fost schimbată.
    const needsGdpr =
      !u.gdprAcceptedAt || (u.gdprCurrentVersion && u.gdprVersion !== u.gdprCurrentVersion);
    if (needsGdpr) {
      setOnboardingStep('gdpr');
      return;
    }

    // Step 1: password change
    if (storedUser.firstLogin === true && u.firstLogin === true) {
      setOnboardingStep('password');
      return;
    }

    // Pre-fetch o singură dată statusul AdES (CNP setat? cheie generată?) pentru a putea
    // grupa pașii în mod inteligent: CNP înainte de semnătură, apoi semnătura + AdES în
    // același modal dacă cheia lipsește.
    let signingStatus = null;
    if (u.role !== 'SUPER_ADMIN') {
      try {
        const signingRes = await api.get('/signing/status');
        signingStatus = signingRes.data;
      } catch (err) {
        console.error('Signing status check error:', err);
      }
    }

    // Step 2: CNP — necesar pentru certificatul AdES (Subject DN cu cnpHash).
    // Mutat ÎNAINTEA semnăturii pentru ca pasul de semnătură să poată activa AdES în
    // același pop-up (parola e introdusă o singură dată acolo).
    // Folosim sessionStorage „cnpSkipped" ca să respectăm butonul „Mai târziu" în sesiunea
    // curentă (la fel ca traseul) — altfel checkFirstTimeSetup redeschide imediat modalul.
    const cnpSkipped = sessionStorage.getItem(`cnpSkipped_${u.id}`) === 'true';
    // CNP-ul e necesar pentru TOATE rolurile cu companyId — inclusiv rolurile SSM
    // (SSM_COMPANY_ADMIN, SSM_INSTRUCTOR), care semnează fișe IIG și au nevoie de CNP pentru
    // semnătura electronică avansată (AdES). Backend-ul creează automat rândul de Employee la
    // `PUT /employees/me` cu { cnp }, deci nu mai există deadlock. Excludem doar SUPER_ADMIN
    // (nu are companyId și nu semnează fișe).
    const cnpAppliesToRole = u.role !== 'SUPER_ADMIN' && !!u.companyId;
    if (signingStatus?.missingCNP && !cnpSkipped && cnpAppliesToRole) {
      setOnboardingStep('cnp');
      return;
    }

    // Step 2b: semnătură vizuală (eventual cu AdES activat în același modal)
    // Respectăm „Mai târziu" în sesiunea curentă (revine la next login/reload).
    const signatureSkipped = sessionStorage.getItem(`signatureSkipped_${u.id}`) === 'true';
    try {
      const sigRes = await api.get('/signatures/my');
      if (!sigRes.data.hasConfiguredSignature && !signatureSkipped) {
        const needsAdES = !!(signingStatus && !signingStatus.hasSigningKey);
        setSignatureNeedsAdES(needsAdES);
        setOnboardingStep('signature');
        return;
      }
      if (sigRes.data.hasConfiguredSignature) {
        localStorage.setItem(`signaturePrompt_${user.id}`, 'true');
      }
    } catch (err) {
      console.error('Signature check error:', err);
    }

    // Step 2c: fallback — userul are deja semnătură vizuală dar nu are cheia AdES
    // (caz tipic pentru utilizatorii existenți la migrarea la AdES). Modalul separat
    // rămâne ca să nu trebuiască să șteargă semnătura ca să activeze AdES.
    if (signingStatus && !signingStatus.hasSigningKey) {
      setOnboardingStep('signingKey');
      return;
    }

    // Step 3: route (only for roles that need it)
    // Folosim sessionStorage ca să respectăm „Mai târziu" în sesiunea curentă — altfel
    // checkFirstTimeSetup ar redeschide modalul imediat (vede iar că lipsește traseul).
    // sessionStorage se șterge la reload / next login, deci modalul revine atunci.
    const needsRoute = u.role !== 'SUPER_ADMIN';
    const routeSkipped = sessionStorage.getItem(`routeSkipped_${u.id}`) === 'true';
    if (needsRoute && !routeSkipped) {
      try {
        const routeRes = await api.get('/route/me');
        if (!routeRes.data.homeAddress || !routeRes.data.workAddress) {
          if (window.location.pathname !== '/my-route') {
            setOnboardingStep('route');
            return;
          }
        }
      } catch (err) {
        console.error('Route check error:', err);
      }
    }

    // Step 4: change log — declarații schimbare nume/traseu pendente la semnătura angajatului.
    // Blocant: nu poate folosi platforma până nu semnează sau se deloghează manual.
    try {
      const pendingRes = await api.get('/change-logs/pending-for-me');
      const mine = pendingRes.data?.asEmployee || [];
      if (mine.length > 0) {
        setOnboardingStep('changeSignature');
        return;
      }
    } catch (err) {
      console.error('Pending change logs check error:', err);
    }

    // Step 5: wizard de onboarding asistat.
    // 2 căi de activare:
    //  A) Manual din MyProfile („Redeschide ghidul") — setează sessionStorage flag.
    //  B) Auto pentru useri noi la firmele care au bifat `showOnboardingWizard` (OPT-IN per firmă).
    //     SSM-istul decide pentru fiecare client. Se afișează doar dacă userul nu l-a finalizat
    //     încă (onboardingCompletedAt == null). Setările blocante de mai sus rulează primele.
    try {
      if (sessionStorage.getItem('reopenOnboardingWizard') === 'true') {
        sessionStorage.removeItem('reopenOnboardingWizard');
        setOnboardingStep('wizard');
        return;
      }
    } catch (_) {
      /* private mode */
    }

    if (u.showOnboardingWizard === true && !u.onboardingCompletedAt) {
      setOnboardingStep('wizard');
      return;
    }
  };

  const getNavigation = (user) => {
    const dashboardItem = {
      name: t('nav.dashboard'),
      href: '/dashboard',
      icon: NavIcons.dashboard,
      badge: issuesCriticalCount > 0 ? issuesCriticalCount : null,
    };
    if (!user) return [{ section: null, items: [dashboardItem] }];

    const ipDashboardItem = {
      name: t('nav.ipDashboard'),
      href: '/dashboard-ip',
      icon: NavIcons.semafor,
    };
    const coursesItem = { name: t('nav.courses'), href: '/courses', icon: NavIcons.courses };
    const jobDescriptionsItem = {
      name: t('nav.jobDescriptions', 'Fișa postului'),
      href: '/job-descriptions/templates',
      icon: NavIcons.sheets,
    };
    const assignCoursesItem = {
      name: t('nav.assignCourses'),
      href: '/assign-courses',
      icon: NavIcons.assign,
    };
    const clientCompaniesItem = {
      name: t('nav.clientCompanies'),
      href: '/companies',
      icon: NavIcons.companies,
    };
    const myCoursesItem = { name: t('nav.myCourses'), href: '/my-courses', icon: NavIcons.courses };
    const myCertificatesItem = {
      name: t('nav.myCertificates'),
      href: '/my-certificates',
      icon: NavIcons.certificates,
    };
    const sheetsToSignItem = {
      name: t('nav.sheetsToSign'),
      href: '/sheets-to-sign',
      icon: NavIcons.sheets,
    };

    if (user.role === 'SUPER_ADMIN') {
      return [
        { section: t('nav.sections.overview'), items: [dashboardItem, ipDashboardItem] },
        {
          section: t('nav.sections.administration'),
          items: [
            { name: t('nav.ssmCompanies'), href: '/ssm-companies', icon: NavIcons.ssmCompany },
            clientCompaniesItem,
            { name: t('nav.search'), href: '/search', icon: NavIcons.search },
            { name: t('nav.globalReports'), href: '/reports', icon: NavIcons.reports },
            { name: t('nav.audit') || 'Audit log', href: '/audit', icon: NavIcons.search },
          ],
        },
        { section: t('nav.sections.training'), items: [coursesItem] },
      ];
    }

    if (user.role === 'SSM_COMPANY_ADMIN') {
      return [
        { section: t('nav.sections.overview'), items: [dashboardItem, ipDashboardItem] },
        {
          section: t('nav.sections.organization'),
          items: [
            ...(user.companyId
              ? [
                  {
                    name: t('nav.mySSMCompany'),
                    href: `/companies/${user.companyId}`,
                    icon: NavIcons.ssmCompany,
                  },
                ]
              : []),
            clientCompaniesItem,
            {
              name: t('nav.reports') || t('nav.globalReports'),
              href: '/reports',
              icon: NavIcons.reports,
            },
            { name: t('nav.audit') || 'Audit log', href: '/audit', icon: NavIcons.search },
          ],
        },
        {
          section: t('nav.sections.training'),
          items: [coursesItem, jobDescriptionsItem, assignCoursesItem, sheetsToSignItem],
        },
        // SSM-iștii pot face cursuri ei înșiși și să-și vadă propriile certificate.
        { section: t('nav.sections.personal'), items: [myCoursesItem, myCertificatesItem] },
      ];
    }

    if (user.role === 'SSM_INSTRUCTOR') {
      return [
        { section: t('nav.sections.overview'), items: [dashboardItem, ipDashboardItem] },
        {
          section: t('nav.sections.organization'),
          items: [
            clientCompaniesItem,
            {
              name: t('nav.reports') || t('nav.globalReports'),
              href: '/reports',
              icon: NavIcons.reports,
            },
          ],
        },
        {
          section: t('nav.sections.training'),
          items: [coursesItem, jobDescriptionsItem, assignCoursesItem, sheetsToSignItem],
        },
        { section: t('nav.sections.personal'), items: [myCoursesItem, myCertificatesItem] },
      ];
    }

    if (user.role === 'COMPANY_ADMIN') {
      const canAssign = canAssignAnyCourse(user, false);
      const employeesItem = {
        name: t('nav.employees'),
        href: '/employees',
        icon: NavIcons.employees,
      };
      const departmentsItem = {
        name: t('nav.departments') || 'Departamente',
        href: '/departments',
        icon: NavIcons.companies,
      };
      const reportsItem = {
        name: t('nav.reports') || t('nav.globalReports'),
        href: '/reports',
        icon: NavIcons.reports,
      };
      // Adminul firmei nu vede biblioteca de „Cursuri" (alea simple, partajate) — el își vede
      // cursurile lui asignate prin „Cursurile mele". Cursurile partajate țin de SSM-iști.
      return [
        {
          section: t('nav.sections.personal'),
          items: [dashboardItem, myCoursesItem, myCertificatesItem],
        },
        {
          section: t('nav.sections.myCompany'),
          items: [
            ...(user.companyId
              ? [
                  {
                    name: t('nav.myCompany'),
                    href: `/companies/${user.companyId}`,
                    icon: NavIcons.companies,
                  },
                ]
              : []),
            ipDashboardItem,
            employeesItem,
            departmentsItem,
            ...(canAssign ? [assignCoursesItem] : []),
            sheetsToSignItem,
            reportsItem,
            { name: t('nav.audit') || 'Audit log', href: '/audit', icon: NavIcons.search },
          ],
        },
      ];
    }

    if (user.role === 'DIRECTOR_GENERAL') {
      // Directorul general e rolul executiv intern — vede tot ce ține de managementul firmei lui
      // (angajați, departamente, fișe de semnat, rapoarte), pe lângă zona personală de cursuri.
      const employeesItem = {
        name: t('nav.employees'),
        href: '/employees',
        icon: NavIcons.employees,
      };
      const departmentsItem = {
        name: t('nav.departments') || 'Departamente',
        href: '/departments',
        icon: NavIcons.companies,
      };
      const reportsItem = {
        name: t('nav.reports') || t('nav.globalReports'),
        href: '/reports',
        icon: NavIcons.reports,
      };
      const canAssign = canAssignAnyCourse(user, false);
      return [
        {
          section: t('nav.sections.personal'),
          items: [dashboardItem, myCoursesItem, myCertificatesItem],
        },
        {
          section: t('nav.sections.management'),
          items: [
            ...(user.companyId
              ? [
                  {
                    name: t('nav.myCompany'),
                    href: `/companies/${user.companyId}`,
                    icon: NavIcons.companies,
                  },
                ]
              : []),
            ipDashboardItem,
            employeesItem,
            departmentsItem,
            coursesItem,
            ...(canAssign ? [assignCoursesItem] : []),
            sheetsToSignItem,
            reportsItem,
            // Audit log NU pentru DIRECTOR_GENERAL — ruta /audit e blocată pentru el (vezi App.js).
            // Linkul ducea pe dashboard → link mort. Bug #6.
          ],
        },
      ];
    }

    if (user.role === 'EMPLOYEE') {
      // CLM = responsabil de departament (managerId) SAU flag de mandat (isCLM, ex. „pe toată firma"
      // / adăugat din view). Ambele dau aceleași drepturi de meniu (fișe de semnat, atribuire, oameni).
      const isClm = managesDepartment || user?.isCLM === true;
      const canAssign = canAssignAnyCourse(user, isClm);
      const canSignSheets = isClm || user.isSSMInstructor || user.isCadruTehnicPSI;
      // Fișa postului: Inspector SSM (flag) sau Cadru Tehnic PSI (flag) pot edita template-ul
      // firmei lor SSM (aliniat cu permisiunea SSM_COMPANY_ADMIN/SSM_INSTRUCTOR).
      const canManageJobDescription = user.isSSMInstructor || user.isCadruTehnicPSI;
      // HR (canManageEmployees) și CLM gestionează angajați → văd secțiunile „Angajați"/„Departamente".
      const canManagePeople = user.canManageEmployees || isClm;
      const employeesItem = {
        name: t('nav.employees'),
        href: '/employees',
        icon: NavIcons.employees,
      };
      const departmentsItem = {
        name: t('nav.departments') || 'Departamente',
        href: '/departments',
        icon: NavIcons.companies,
      };
      const managementItems = [
        ...((user.canManageEmployees || isClm) && user.companyId
          ? [
              {
                name: t('nav.myCompany'),
                href: `/companies/${user.companyId}`,
                icon: NavIcons.companies,
              },
            ]
          : []),
        ...(canManagePeople ? [ipDashboardItem, employeesItem, departmentsItem] : []),
        ...(isClm ? [coursesItem] : []),
        ...(canManageJobDescription ? [jobDescriptionsItem] : []),
        ...(canAssign ? [assignCoursesItem] : []),
        ...(canSignSheets ? [sheetsToSignItem] : []),
      ];
      return [
        {
          section: t('nav.sections.personal'),
          items: [dashboardItem, myCoursesItem, myCertificatesItem],
        },
        ...(managementItems.length
          ? [{ section: t('nav.sections.management'), items: managementItems }]
          : []),
      ];
    }

    return [{ section: null, items: [dashboardItem] }];
  };

  const navigation = getNavigation(user);
  const isActive = (path) => location.pathname === path;

  const renderNavLink = (item) => (
    <Link
      key={item.href}
      to={item.href}
      onClick={() => setSidebarOpen(false)}
      className={`${isActive(item.href) ? 'sidebar-item-active' : 'sidebar-item-default'} flex items-center justify-between`}
    >
      <span className="flex items-center gap-2">
        {item.icon}
        <span>{item.name}</span>
      </span>
      {item.badge != null && (
        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-danger-500 text-white">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-surface-secondary">
      {/* GDPR modal — first step, blocks everything until accepted (or logout on refuse) */}
      {onboardingStep === 'gdpr' && (
        <GDPRModal
          onAccepted={async () => {
            const fresh = await refreshUser();
            setOnboardingStep(null);
            checkFirstTimeSetup(fresh);
          }}
        />
      )}

      {/* Password modal -- single instance */}
      {onboardingStep === 'password' && (
        <ChangePasswordModal
          onClose={() => {}}
          onSuccess={() => {
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
        />
      )}

      {onboardingStep === 'signature' && (
        <SignatureSetupModal
          isFirstTime={true}
          requireAdESPassword={signatureNeedsAdES}
          onLater={() => {
            // „Mai târziu" — skip pentru sesiunea curentă ca să nu reapară imediat.
            if (user?.id) sessionStorage.setItem(`signatureSkipped_${user.id}`, 'true');
            setSignatureNeedsAdES(false);
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
          onSuccess={() => {
            localStorage.setItem(`signaturePrompt_${user.id}`, 'true');
            setSignatureNeedsAdES(false);
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
        />
      )}

      {onboardingStep === 'cnp' && (
        <CNPModal
          onLater={() => {
            // „Mai târziu" — skip pentru sesiunea curentă, ca să nu reapară imediat la
            // următorul checkFirstTimeSetup. Revine la următorul login/reload.
            if (user?.id) sessionStorage.setItem(`cnpSkipped_${user.id}`, 'true');
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
          onSuccess={() => {
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
        />
      )}

      {onboardingStep === 'signingKey' && (
        <SigningKeyActivationModal
          onSuccess={() => {
            setOnboardingStep(null);
            checkFirstTimeSetup();
          }}
        />
      )}

      {onboardingStep === 'changeSignature' && (
        <ChangeSignaturePopup
          onDone={async () => {
            // Refresh user pentru ca numele actualizat să apară în UI fără refresh manual
            const fresh = await refreshUser();
            setOnboardingStep(null);
            checkFirstTimeSetup(fresh);
          }}
        />
      )}

      {onboardingStep === 'route' && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md w-full mx-4">
            <div className="text-center mb-4">{NavIcons.sign}</div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 text-center mb-2">
              {t('layout.routeModal.title')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-center mb-6 text-sm">
              {t('layout.routeModal.desc')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setOnboardingStep(null);
                  // Flag pentru ca Layout să re-ruleze `checkFirstTimeSetup` când userul revine
                  // pe /dashboard după salvarea traseului — altfel chain-ul de onboarding se
                  // rupe aici și wizard-ul (ultimul pas) nu apare decât la următorul reload.
                  sessionStorage.setItem('continueOnboardingAfterRoute', 'true');
                  navigate('/my-route', { state: { isOnboarding: true } });
                }}
                className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                {t('layout.routeModal.fillNow')}
              </button>
              <button
                onClick={() => {
                  // Marchează skip pentru sesiunea curentă, ca să nu re-deschidem modalul
                  // imediat când checkFirstTimeSetup se rulează din nou pentru a continua cu wizardul.
                  if (user?.id) sessionStorage.setItem(`routeSkipped_${user.id}`, 'true');
                  setOnboardingStep(null);
                  // Defer la următorul tick ca pop-up-ul de traseu să se închidă VIZUAL înainte
                  // ca wizard-ul să se deschidă (cerință: „wizardul porneşte DUPĂ ce ies din pop-up").
                  // Fără setTimeout, React batch-uia setOnboardingStep(null) cu setOnboardingStep('wizard')
                  // din checkFirstTimeSetup → modalul se transforma direct în wizard, fără tranziție vizibilă.
                  setTimeout(() => checkFirstTimeSetup(), 0);
                }}
                className="py-3 px-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {t('layout.routeModal.later')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding wizard — randat când step-state-machine setează 'wizard'.
          Decizia de afișare se ia în `checkFirstTimeSetup` (gate-uri: showOnboardingWizard
          opt-in al firmei sau reopen manual din profil). */}
      {onboardingStep === 'wizard' && (
        <OnboardingWizard
          onClose={async () => {
            setOnboardingStep(null);
            // Refresh user ca onboardingCompletedAt să fie reflectat în context (eventuala
            // redeschidere ulterioară prin profil va folosi /onboarding/reset).
            try {
              await refreshUser();
            } catch (_) {
              /* best-effort */
            }
          }}
        />
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 dark:bg-black/70 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-800 transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Top sidebar — logo platformă FIX, mereu în același loc (poziția originală, sus-stânga).
            Logo-ul firmei SSM (white-label) NU mai înlocuiește brandul platformei aici — apare
            separat, jos-stânga în sidebar (decizie owner 2026-07-08). */}
        <div className="h-14 flex items-center px-5 border-b border-slate-200 dark:border-slate-800">
          <img
            src={logo}
            alt="Platformă SSM"
            onClick={() => navigate('/dashboard')}
            className="h-12 w-auto cursor-pointer"
          />
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/50 rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {user?.companyName}
              </p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {/* Rolul REAL în firmă (nu cel efectiv). Un împuternicit admin vede meniul de admin,
                dar tag-ul rămâne rolul lui real; „Împuternicit admin" apare separat în UserLabelBadges. */}
            <RoleBadge role={user?.realRole || user?.role} />
            <UserLabelBadges user={user} />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto min-h-0">
          {navigation.map((group, idx) =>
            group.section ? (
              <NavSection key={group.section} title={group.section}>
                {group.items.map(renderNavLink)}
              </NavSection>
            ) : (
              <div key={`g-${idx}`} className="space-y-1 mb-2">
                {group.items.map(renderNavLink)}
              </div>
            )
          )}

          {/* Cont — continuitate cu restul categoriilor meniului (același stil NavSection) */}
          <NavSection title={t('nav.account') || 'Cont'}>
            {/* Notificările sunt accesibile DOAR din clopoțelul din bara de sus (NotificationBell).
                Linkul din sidebar a fost scos — owner a cerut clopoțel doar sus, nu duplicat în stânga. */}
            <Link
              to="/manual"
              onClick={() => setSidebarOpen(false)}
              className={isActive('/manual') ? 'sidebar-item-active' : 'sidebar-item-default'}
            >
              {NavIcons.manual}
              <span>{t('nav.manual')}</span>
            </Link>
            <Link
              to="/my-profile"
              onClick={() => setSidebarOpen(false)}
              className={isActive('/my-profile') ? 'sidebar-item-active' : 'sidebar-item-default'}
            >
              {NavIcons.profile}
              <span>{t('nav.myProfile')}</span>
            </Link>
            <button
              onClick={logout}
              className="sidebar-item-default w-full text-slate-500 dark:text-slate-400 hover:text-danger-600 dark:hover:text-danger-300"
            >
              {NavIcons.logout}
              <span>{t('nav.logout')}</span>
            </button>
          </NavSection>
        </nav>

        {/* Link-uri legale jos-stânga, listate vertical compact */}
        <div className="hidden md:flex flex-col items-start flex-shrink-0 px-5 pt-3 pb-4 border-t border-slate-100 dark:border-slate-800">
          <div className="flex flex-col gap-1 text-xs leading-tight text-slate-400 dark:text-slate-500 w-full">
            <Link
              to="/legal/privacy"
              className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              {t('legal.privacy')}
            </Link>
            <Link
              to="/legal/cookies"
              className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              {t('legal.cookies')}
            </Link>
            <Link
              to="/legal/terms"
              className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              {t('legal.terms')}
            </Link>
            <Link
              to="/legal/about"
              className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              {t('legal.about')}
            </Link>
            <Link
              to="/legal/contact"
              className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              {t('legal.contact')}
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-56">
        <ImpersonationBanner />
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2 px-4 lg:px-6">
          {/* Left */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
              aria-label={t('layout.openMenu')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <h1
              onClick={() => navigate('/dashboard')}
              className="text-lg font-semibold text-slate-900 dark:text-slate-100 cursor-pointer hover:text-primary-600 dark:hover:text-primary-300 transition-colors truncate flex items-center gap-2 min-w-0"
            >
              {/* Logo firmă SSM (white-label) — înaintea numelui firmei, bara de sus. Apare
                  doar dacă firma SSM proprie are logo încărcat. */}
              {ssmLogoPath && (
                <img
                  src={logoUrl(ssmLogoPath)}
                  alt={ssmFirmCompany?.name || 'SSM'}
                  className="h-12 w-auto flex-shrink-0 object-contain"
                  title={ssmFirmCompany?.name}
                />
              )}
              <span className="truncate">{user?.companyName || 'Dashboard'}</span>
            </h1>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Logo FIRMĂ CLIENT — lângă butonul de temă. Apare doar când user-ul e la
                o firmă client (nu SSM) și firma are un logo încărcat. Ascuns pe mobil ca
                să nu îngrămădească bara de sus la 375px. */}
            {clientLogoPath && (
              <img
                src={logoUrl(clientLogoPath)}
                alt={clientCompany?.name || 'Logo firmă'}
                className="hidden sm:block h-7 max-w-[120px] object-contain"
                title={clientCompany?.name}
              />
            )}
            {/* Search global Ctrl+K — buton vizibil ca să fie descoperibil; tooltip arată shortcut-ul */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={t('globalSearch.tooltip')}
              aria-label={t('globalSearch.tooltip')}
              data-onboarding="global-search"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
            </button>
            <ThemeToggle />
            {/* Pe mobil folosim varianta compactă (un singur steag) ca să încapă bara la 375px;
                pe ecrane ≥sm afișăm comutatorul complet RO/EN. */}
            <span className="sm:hidden">
              <LanguageSwitcher compact />
            </span>
            <span className="hidden sm:inline-flex">
              <LanguageSwitcher />
            </span>
            <NotificationBell />
          </div>
        </header>

        {/* Modal Ctrl+K — montat la nivel de Layout ca să fie disponibil din orice pagină */}
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
